import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col } from "reactstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { formatHeader, GROUPING_OPTIONS, INCIDENT_CONFIG } from "./helper";

const IncidentAnalysis = ({
  groupedTickets,
  uniqueIncidentTypes,
  searchIncidentType,
}) => {
  const [incidentStats, setIncidentStats] = useState({});
  const [additionalStats, setAdditionalStats] = useState({});
const [groupBy, setGroupBy]=useState("Division")
  console.log("groupedTickets", groupedTickets);

  useEffect(() => {
    if (groupedTickets.length > 0) {
      analyzeIncidents();
      analyzeAdditionalFactors();
    }
  }, [groupedTickets, searchIncidentType]);

  const getIncidentConfig = (incidentName) => {
    for (const key in INCIDENT_CONFIG) {
      if (incidentName?.includes(key)) {
        return INCIDENT_CONFIG[key];
      }
    }
    return INCIDENT_CONFIG["Other"];
  };

  const analyzeIncidents = () => {
    const incidentTypeGroups = {};

    uniqueIncidentTypes.forEach(({ incidentName, incidentTypeId }) => {
      incidentTypeGroups[incidentTypeId] = {
        name: incidentName,
        count: 0,
        statusCounts: { open: 0, inProgress: 0, resolved: 0 },
        tickets: [],
        areaData: {},
      };
    });


    setGroupBy(groupedTickets[0].group)
    groupedTickets?.forEach((group) => {
      Object.entries(group?.incidentTypeCounts || {}).forEach(
        ([incidentTypeId, data]) => {
          const entry = incidentTypeGroups[incidentTypeId];
          if (entry) {
            entry.count += data?.count || 0;
            entry.statusCounts.open += data.openCount || 0;
            entry.statusCounts.inProgress += data.inProgressCount || 0;
            entry.statusCounts.resolved += data.resolvedCount || 0;
            entry.areaData[group.name] = {
              count: data?.count || 0,
              ...(group?.totalBurnedArea > 0 &&
                entry.name === "Forest Fire" && {
                  burnedArea: parseFloat(group?.totalBurnedArea.toFixed(2)),
                }),
            };
          }
        }
      );
    });

    setIncidentStats(incidentTypeGroups);
  };

  // Analyze additional factors based on the groupedTickets structure
  const analyzeAdditionalFactors = () => {
    if (!groupedTickets || groupedTickets.length === 0) return;

    const statsObj = {};

    // Initialize stats object for each grouping option
    GROUPING_OPTIONS.forEach((option) => {
      if (option.id !== "incidentType") {
        statsObj[option.id] = {
          items: {},
          areaBreakdown: {},
        };
      }
    });

    // Process each group and its category counts
    groupedTickets.forEach((group) => {
      const area = group.name || "Unknown";
      const categoryCounts = group.catergotyCounts || {};

      // Process each grouping option for this group
      GROUPING_OPTIONS.forEach((option) => {
        if (option.id !== "incidentType") {
          const categoryData = categoryCounts[option.id] || {};

          // Initialize area in areaBreakdown if not exists
          if (!statsObj[option.id].areaBreakdown[area]) {
            statsObj[option.id].areaBreakdown[area] = {};
          }

          // Process each item in the category
          Object.entries(categoryData).forEach(([itemKey, itemData]) => {
            const count = itemData.count || 0;

            // Update total count for this item
            statsObj[option.id].items[itemKey] =
              (statsObj[option.id].items[itemKey] || 0) + count;

            // Update area breakdown for this item
            statsObj[option.id].areaBreakdown[area][itemKey] =
              (statsObj[option.id].areaBreakdown[area][itemKey] || 0) + count;
          });
        }
      });
    });

    setAdditionalStats(statsObj);
  };

  // Prepare consolidated chart data for all incident types
  const prepareConsolidatedChartData = () => {
    const areaNames = new Set();

    // First collect all area names across all incident types
    Object.values(incidentStats).forEach((incident) => {
      Object.keys(incident.areaData).forEach((area) => areaNames.add(area));
    });

    const chartData = [];

    // Then create area-centric data structure
    Array.from(areaNames).forEach((area) => {
      const areaData = { area };
      let hasData = false;

      // Add data for incident types based on selection
      const relevantTypes = searchIncidentType
        ? uniqueIncidentTypes.filter(
            (type) => type.incidentTypeId === searchIncidentType
          )
        : uniqueIncidentTypes;

      relevantTypes.forEach((type) => {
        const incidentTypeId = type.incidentTypeId;
        const stats = incidentStats[incidentTypeId];
        if (stats && stats.areaData[area]) {
          // Only add non-zero values
          const count = stats.areaData[area]?.count || 0;
          if (count > 0) {
            areaData[`${type.incidentName}`] = count;

            // For forest fires, include burned area in the same data point when type is 1
            if (
              type.incidentName === "Forest Fire" &&
              searchIncidentType === "1" &&
              stats.areaData[area].burnedArea &&
              stats.areaData[area].burnedArea > 0
            ) {
              areaData[`${type.incidentName}_burnedArea`] =
                stats.areaData[area].burnedArea;
            }

            hasData = true;
          }
        }
      });

      // Only add areas that have at least one non-zero value
      if (hasData) {
        chartData.push(areaData);
      }
    });

    return chartData;
  };

  // Prepare chart data for additional grouping options
  const prepareAdditionalChartData = (optionId) => {
    if (!additionalStats[optionId]) return [];
    const areaNames = new Set(
      Object.keys(additionalStats[optionId].areaBreakdown)
    );
    const itemNames = new Set(Object.keys(additionalStats[optionId].items));

    const chartData = [];
    Array.from(areaNames).forEach((area) => {
      const areaData = { area };
      let hasData = false;

      Array.from(itemNames).forEach((item) => {
        const count =
          additionalStats[optionId].areaBreakdown[area]?.[item] || 0;
        if (count > 0) {
          areaData[item] = count;
          hasData = true;
        }
      });

      if (hasData) {
        chartData.push(areaData);
      }
    });

    return chartData;
  };

  // Get colorful colors for the chart
  const getColorfulColor = (index) => {
    // Vibrant, colorful palette
    const colors = [
      "#FF6B6B", // Red
      "#4ECDC4", // Teal
      "#FFD166", // Yellow
      "#6A0572", // Purple
      "#1A936F", // Green
      "#3D5A80", // Navy Blue
      "#F86624", // Orange
      "#662E9B", // Deep Purple
      "#43BCCD", // Sky Blue
      "#F26419", // Bright Orange
      "#86BBD8", // Light Blue
      "#2FBF71", // Bright Green
    ];
    return colors[index % colors.length];
  };

  if (groupedTickets.length === 0) {
    return (
      <div className="text-center p-4">
        No incident data available for analysis.
      </div>
    );
  }

  const consolidatedChartData = prepareConsolidatedChartData();

  // Custom label formatter for values
  const renderCustomizedLabel = (props) => {
    const { x, y, width, height, value } = props;
    if (!value) return null;

    return (
      <text
        x={x + width / 2}
        y={y - 10}
        fill="#000000"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={12}
      >
        {value}
      </text>
    );
  };

  // Custom tooltip to show both count and burned area for forest fires
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{
            background: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
          }}
        >
          <p className="label">{`Area: ${label}`}</p>
          {payload.map((entry, index) => {
            const isCount = !entry.dataKey.includes("_burnedArea");
            return (
              <p key={`item-${index}`} style={{ color: entry.color }}>
                {isCount
                  ? `${entry.name}: ${entry.value}`
                  : `Burned Area: ${entry.value} ha`}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Filter incident types based on selection
  const relevantIncidentTypes = searchIncidentType
    ? uniqueIncidentTypes.filter(
        (type) => type.incidentTypeId === searchIncidentType
      )
    : uniqueIncidentTypes;

  // Render area breakdown tables for additional statistics
  const renderAreaBreakdownTable = (optionId) => {
    if (!additionalStats[optionId]) return null;

    const option = GROUPING_OPTIONS.find((opt) => opt.id === optionId);
    const itemNames = Object.keys(additionalStats[optionId].items);
    const areaNames = Object.keys(additionalStats[optionId].areaBreakdown);
    const totalCount = Object.values(additionalStats[optionId].items).reduce(
      (acc, val) => acc + val,
      0
    );

    return (
      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th style={{ fontSize: "18px" }}>{groupBy}</th>
              {itemNames.map((item) => (
                <th key={item} style={{ fontSize: "18px" }}>
                  {formatHeader(item)}
                </th>
              ))}
              <th style={{ fontSize: "18px" }}>Total Incident/Ticket</th>
              <th style={{ fontSize: "18px" }}>Percentage</th>
            </tr>
          </thead>
          <tbody>
  {areaNames
    .filter(area => {
      // Calculate area total to check if it's greater than 0
      const areaData = additionalStats[optionId].areaBreakdown[area];
      const areaTotal = itemNames.reduce(
        (acc, item) => acc + (areaData?.[item] || 0),
        0
      );
      return areaTotal>0 ; // Only include areas with total > 0
    })
    .map((area) => {
      const areaData = additionalStats[optionId].areaBreakdown[area];
      const areaTotal = itemNames.reduce(
        (acc, item) => acc + (areaData?.[item] || 0),
        0
      );
      const percentage =
        totalCount > 0 ? Math.round((areaTotal / totalCount) * 100) : 0;

      return (
        <tr key={area}>
          <td>
            <strong style={{ fontSize: "18px",textTransform:"capitalize" }}>{area}</strong>
          </td>
          {itemNames.map((item) => (
            <td key={item}style={{ fontSize: "17px",textTransform:"capitalize" }}>{areaData?.[item]||0}</td>
          ))}
          <td style={{ fontSize: "17px",textTransform:"capitalize" }}>{areaTotal}</td>
          <td style={{ fontSize: "17px",textTransform:"capitalize" }}>{percentage}%</td>
        </tr>
      );
    })}

            <tr>
              <td>
                <strong style={{ fontSize: "18px" }}>Total</strong>
              </td>
              {itemNames.map((item) => (
                <td key={item} style={{ fontSize: "17px",textTransform:"capitalize" }}>
                  <strong>{additionalStats[optionId].items[item] || 0}</strong>
                </td>
              ))}
              <td>
                <strong style={{ fontSize: "17px",textTransform:"capitalize" }}>{totalCount}</strong>
              </td>
              <td>
                <strong>100%</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Render incident type charts
  const renderIncidentTypeCharts = () => {
    console.log("incidentStats',incidentStats",incidentStats)
    return (
      <>
      <Card >
        <CardBody>
        {/* Incident Types Chart */}
        <Row className=" incident-analysis">
        <h5 className="mb-5 d-flex justify-content-center">Total Incident/Ticket by {groupBy}</h5>

          <Col md={12}  className="mb-4">
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={consolidatedChartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 80,
                }}
                barCategoryGap={10}
                barGap={0}
              >
                <XAxis
                  dataKey="area"
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  height={100}
                  tick={{
                    fontSize: 14,
                    fontWeight: "bold",
                  }}
                  tickFormatter={(value) => value?.charAt(0).toUpperCase() + value?.slice(1)}

                  label={{
                    value: groupBy+"s",
                    position: "insideBottom",
                    offset: -5,
                    fontSize: 16,
                    fontWeight: "bold",
                  }}
                />
                <YAxis allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  layout="horizontal"
                  verticalAlign="top"
                  align="center"
                  wrapperStyle={{ paddingBottom: 20 }}
                />

                {relevantIncidentTypes.map((type, index) => {
                  const fillColor = getColorfulColor(index);
                  // For forest fires with type 1, we'll use a gradient fill to show burned area
                  const isForestFire =
                    type.incidentName === "Forest Fire" &&
                    searchIncidentType === "1";

                  return (
                    <Bar
                      key={type.incidentTypeId}
                      dataKey={type.incidentName}
                      name={
                        isForestFire
                          ? `${type.incidentName} (Count)`
                          : type.incidentName
                      }
                      fill={fillColor}
                      stroke="#000000"
                      strokeWidth={0.5}
                      maxBarSize={35}
                    >
                      <LabelList
                        dataKey={type.incidentName}
                        position="top"
                        content={renderCustomizedLabel}
                      />

                      {/* If it's forest fire type 1, add burned area label */}
                      {isForestFire && (
                        <LabelList
                          dataKey={`${type.incidentName}_burnedArea`}
                          position="insideTop"
                          content={(props) => {
                            const { x, y, width, height, value } = props;
                            if (!value) return null;

                            return (
                              <text
                                x={x + width / 2}
                                y={y + height / 2}
                                fill="#FFFFFF"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={11}
                              >
                                {`${value} ha`}
                              </text>
                            );
                          }}
                        />
                      )}
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </Col>

          {/* Individual incident type analysis */}
          {relevantIncidentTypes
  .filter((type) => incidentStats[type.incidentTypeId]?.count > 0)
  .map((type, index) => {
    const incidentTypeId = type.incidentTypeId;
    const stats = incidentStats[incidentTypeId];
    const config = getIncidentConfig(type.incidentName);
    const IconComponent = config.icon;
    const showBurnedArea =
      type.incidentName === "Forest Fire" &&
      searchIncidentType === "1";
    const fillColor = getColorfulColor(index);
    
    // Use the groupedTickets data for this incident type
    const groupedData = groupedTickets || [];

    return (
      <Col md={12}  className="mb-4" key={incidentTypeId}>
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th style={{ fontSize: "18px" }}>{groupBy}</th>
                <th style={{ fontSize: "18px" }}>Closed</th>
                <th style={{ fontSize: "18px" }}>Resolved</th>
                <th style={{ fontSize: "18px" }}>Total Incident/Ticket</th>
                {showBurnedArea && (
                  <th style={{ fontSize: "18px" }}>
                    Burned Area (ha)
                  </th>
                )}
                <th style={{ fontSize: "18px" }}>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {groupedData.map((groupItem) => {
                // Filter data for current incident type if needed
                const isRelevantIncident = groupItem.incidentTypeCounts && 
                  groupItem.incidentTypeCounts[incidentTypeId];
                
                if (!isRelevantIncident) return null;
                
                const count = groupItem.count || 0;
                const resolvedCount = groupItem.resolvedCount || 0;
                const closedCount = groupItem.closedCount || 0;
                const inProgressCount = groupItem.inProgressCount || 0;
                const burnedArea = groupItem.totalBurnedArea?.toFixed(2) || 0;
                
                const total = stats?.count || 0;
                const percentage =
                  total > 0
                    ? Math.round((count / total) * 100)
                    : 0;

                return (
                  <tr key={groupItem.name}>
                    <td>
                      <strong style={{ fontSize: "18px" ,textTransform:'capitalize'}}>
                        {groupItem.name}
                      </strong>
                    </td>
                    <td style={{ fontSize: "17px",textTransform:"capitalize" }}>{closedCount}</td>
                    <td style={{ fontSize: "17px",textTransform:"capitalize" }}>{resolvedCount}</td>
                    <td style={{ fontSize: "17px",textTransform:"capitalize" }}>{count}</td>
                    {showBurnedArea && (
                      <td>{burnedArea}</td>
                    )}
                    <td style={{ fontSize: "17px",textTransform:"capitalize" }}>{percentage}%</td>
                  </tr>
                );
              })}

              <tr>
                <td>
                  <strong style={{ fontSize: "18px" }}>
                    Total
                  </strong>
                </td>
                <td>
                  <strong>{groupedData.reduce((sum, item) => sum + (item.closedCount || 0), 0)}</strong>
                </td>
                <td>
                  <strong>{groupedData.reduce((sum, item) => sum + (item.resolvedCount || 0), 0)}</strong>
                </td>
               
                <td>
                  <strong>{stats.count}</strong>
                </td>
                {showBurnedArea && (
                  <td>
                    <strong>
                      {groupedData.reduce((sum, item) => sum + (item.totalBurnedArea || 0), 0).toFixed(2)}
                    </strong>
                  </td>
                )}
                <td>
                  <strong>100%</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Col>
    );
  })}
        </Row>
        </CardBody>
        </Card>
      </>
    );
  };

  // Render charts for additional grouping options
  const renderAdditionalGroupCharts = (optionId) => {
    const groupingOption = GROUPING_OPTIONS.find((opt) => opt.id === optionId);
    const chartData = prepareAdditionalChartData(optionId);

    // Skip rendering if there's no data for this grouping or if it's the incident type
    if (
      optionId === "incidentType" ||
      !additionalStats[optionId] ||
      Object.keys(additionalStats[optionId]?.items || {}).length === 0
    ) {
      return null;
    }

    return (   
      <Card>
        <CardBody>

        
      <Row className=" analysyschart">
          <h5 className="mb-5 d-flex justify-content-center">{groupingOption.label} by {groupBy}</h5>
        {/* Bar Chart */}
      <Col md={12} className="mb-4">
  <ResponsiveContainer width="100%" height={500}>
    <BarChart
      data={chartData}
      margin={{
        top: 20,
        right: 60,
        left: 60,
        bottom: 80,
      }}
      barCategoryGap="40%" // Reduced space between categories
      barGap={0} 
    >
      <XAxis
        dataKey="area"
        angle={-45}
        textAnchor="end"
        interval={0}
        height={100}
        tick={{
          fontSize: 14,
          fontWeight: "bold",
        }}
        tickFormatter={(value) => value?.charAt(0).toUpperCase() + value?.slice(1)}
        label={{
          value: groupBy + "s",
          position: "insideBottom",
          offset: -5,
          fontSize: 16,
          fontWeight: "bold",
        }}
      />
      <YAxis allowDecimals={false} />
      <Tooltip />
      <Legend
        layout="horizontal"
        verticalAlign="top"
        align="center"
        wrapperStyle={{ paddingBottom: 20 }}
      />

      {Object.keys(additionalStats[optionId]?.items || {}).map(
        (item, index) => (
          <Bar
            key={item}
            dataKey={item}
            name={formatHeader(item)}
            fill={
              groupingOption.colors?.[index] || getColorfulColor(index)
            }
            stroke="#000000"
            strokeWidth={0.5}
            maxBarSize={40}
          >
            <LabelList
              dataKey={item}
              position="top"
              content={renderCustomizedLabel}
            />
          </Bar>
        )
      )}
    </BarChart>
  </ResponsiveContainer>
</Col>
        {/* Data Table */}
        <Col md={12}  className="mb-4">
          {renderAreaBreakdownTable(optionId)}
        </Col>
      </Row>
      </CardBody>
      </Card>  
    );
  };

  return (
    <div>
      {/* Render incident type charts */}
      {renderIncidentTypeCharts()}

      {GROUPING_OPTIONS.map((option, index) => renderAdditionalGroupCharts(option.id))}

    </div>
  );
};

export default IncidentAnalysis;
